import React from "react"

import type { CompareBy } from "@/metrics/compare-by"
import type { Annotation } from "@/types"

import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { TestCaseExpectedOutput } from "@/types/backend-contracts"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import type { SimulationWithContext } from "@/utils/backend-mimics/SimulationWithContext"

import {
  ComparedMetricDistributionChart,
  MetricDistributionChart,
} from "../../metric-display"
import type { MetricRecord } from "../records/MetricRecord"
import type { TestResult } from "../types/testResult"

const NO_ANSWER_PLACEHOLDER = "–"
const QUEUED_RUN_STATUS = "QUEUED" as const

export function renderAssignmentsTableAIResultCell(
  metricRecord: MetricRecord,
  simulationOutputRow: RunRowLike,
  metricId: string,
): React.ReactNode {
  const metricOutput = simulationOutputRow.metric_outputs?.[metricId]

  if (!metricOutput) {
    return NO_ANSWER_PLACEHOLDER
  }

  return metricRecord.renderBadge(metricOutput)
}

export function getDefaultKeyboardShortcutHints(): {
  key: string
  label: string
}[] {
  return []
}

export function renderDefaultAssignmentsTableReviewCell(): React.ReactNode {
  return NO_ANSWER_PLACEHOLDER
}

export function renderDefaultAssignmentsTableSubRows(): React.ReactNode {
  return null
}

export function evaluateTranscriptDefault(
  _simulationWithContext?: SimulationWithContext,
  _getCompletionMutation?: unknown,
): Promise<TestResult> {
  return Promise.resolve({
    question: "",
    answer: "",
    explanation: "",
    decision_message_indices: [],
    timestamp: Date.now(),
    transcriptId: "",
  })
}

export function renderInDepthRunReviewDefault(
  metricRecord: MetricRecord,
  metricOutputs: RunMetricOutput[],
  context?: {
    runRows?: RunRowLike[]
    highlightValue?: number | string | null
    onBarClick?: (filter: ChartBarFilter) => void
    activeFilters?: ChartBarFilter[]
  },
): React.ReactNode {
  if (metricOutputs.length === 0) {
    return (
      <div className="text-coval-text-muted">
        No metric data available for this metric.
      </div>
    )
  }

  return (
    <MetricDistributionChart
      metricOutputs={metricOutputs}
      metricRecord={metricRecord}
      highlightValue={context?.highlightValue}
      onBarClick={context?.onBarClick}
      activeFilters={context?.activeFilters}
    />
  )
}

export function renderComparedInDepthRunReviewDefault(
  metricRecord: MetricRecord,
  metricOutputs: RunMetricOutput[],
  runRows: RunRowLike[],
  compareBy: CompareBy,
): React.ReactNode {
  if (metricOutputs.length === 0) {
    return (
      <div className="text-coval-text-muted">
        No metric data available for this metric.
      </div>
    )
  }

  return (
    <ComparedMetricDistributionChart
      metricOutputs={metricOutputs}
      runRows={runRows}
      metricRecord={metricRecord}
      compareBy={compareBy}
    />
  )
}

export function renderDeepDiveDefault(
  renderDeepDive: (
    mo: RunMetricOutput,
    testCase: {
      input_str: string
      expected_behaviors: string[]
      expected_output_json?: TestCaseExpectedOutput | null
    },
    dispatchMessageSelect?: (messageIndex: number | null) => void,
    context?: {
      simulationOutputId?: string
      annotationsForMetric?: Annotation[]
    },
  ) => React.ReactNode,
  mo: RunMetricOutput,
  testCase: {
    input_str: string
    expected_behaviors: string[]
    expected_output_json?: TestCaseExpectedOutput | null
  },
  dispatchMessageSelect?: (messageIndex: number | null) => void,
  context?: {
    simulationOutputId?: string
    annotationsForMetric?: Annotation[]
  },
): React.ReactNode {
  if (!mo) {
    return <div className="text-gray-500">Failed to load metric</div>
  }
  if (
    mo.error_message?.includes(
      "Cannot run audio metrics on a transcript with no audio",
    )
  ) {
    return <div className="text-gray-500">No audio to run metric on.</div>
  }
  if (mo.error_status === MetricErrorStatus.IN_PROGRESS) {
    return <div className="text-gray-500">Metric is still running</div>
  }
  if (
    (mo.error_status as string) === QUEUED_RUN_STATUS ||
    mo.error_status === MetricErrorStatus.IN_QUEUE
  ) {
    return <div className="text-gray-500">Metric is queued for processing.</div>
  }
  if (
    (mo.error_status as string) === "CANCELLED" ||
    (mo.error_status as string) === "RUN_CANCELLED"
  ) {
    return (
      <div className="text-blue-500">
        This run was canceled. No metric data is available.
      </div>
    )
  }
  if (mo.error_status === MetricErrorStatus.SKIPPED) {
    return (
      <div className="text-gray-500">
        Metric was skipped{mo.error_message ? `. ${mo.error_message}` : "."}
      </div>
    )
  }
  if (mo.error_status !== MetricErrorStatus.COMPLETED) {
    return (
      <div className="text-gray-500">
        Metric failed to run
        {mo.error_message ? `. Error: ${mo.error_message}` : "."}
      </div>
    )
  }

  return renderDeepDive(mo, testCase, dispatchMessageSelect, context)
}
